/**
 * Study design selector (plan step B0).
 *
 * A study's `design` says how twins meet the stimulus cuts:
 *
 *   comparative  — every twin sees every cut in one session and compares them.
 *   true_monadic — TRUE_MONADIC_RETIRED. Split-sample monadic: each twin saw
 *                  exactly one cut. Retired 2026-09-13 (twins do not fatigue,
 *                  so the whole justification for splitting the sample is
 *                  absent). Nothing produces it and no code path behaves
 *                  differently for it; the string stays only so persisted
 *                  studies still parse and re-analyse.
 *   twin_monadic — each twin rates every cut in isolated lanes.
 *
 * These values are a persisted contract: never rename them.
 *
 * Everything here is pure so the creation-time preview (frontend), the
 * creation-time gate (backend), and the eventual C4 charge can be derived from
 * one source and cannot drift — the same parity pattern `pricing-constants.ts`
 * uses for Quick Study.
 */

// twin_monadic is reachable only from a stored "monadic" adDesignPreference;
// new studies resolve to comparative. TRUE_MONADIC_RETIRED: `true_monadic`
// stays in the union so persisted studies parse, and is reachable from nothing.
export const STUDY_DESIGNS = [
  "comparative",
  "true_monadic",
  "twin_monadic",
] as const;

export type StudyDesign = (typeof STUDY_DESIGNS)[number];

/** New compiled surveys enumerate study units from the canonical asset catalog.
 * Unmarked frozen surveys retain the historical video-first cut policy. */
export const STIMULUS_IDENTITY_POLICY = "canonical-assets.v1" as const;

/** Absence alone denotes a frozen legacy snapshot; an unknown version is not legacy. */
function readStimulusIdentityPolicy(surveyJson: unknown): typeof STIMULUS_IDENTITY_POLICY | null {
  const config = asRecord(asRecord(surveyJson)._canonicalConfig);
  if (!Object.prototype.hasOwnProperty.call(config, "stimulusIdentityPolicy")) return null;
  if (config.stimulusIdentityPolicy === STIMULUS_IDENTITY_POLICY) return STIMULUS_IDENTITY_POLICY;
  throw new RangeError("Unsupported stimulus identity policy.");
}

/** Research judgment stays upstream; only explicitly typed authority is read. */
export function resolveStudyExposure(args: {
  explicitExposure?: unknown;
  storedExposure?: unknown;
  objectiveExposure?: unknown;
}): "comparison" | "monadic" {
  for (const value of [args.explicitExposure, args.storedExposure, args.objectiveExposure]) {
    if (value === "comparison" || value === "monadic") return value;
  }
  return "comparison";
}

/** A single asset does not need an isolated multi-cut execution design. */
export function studyDesignForExposure(
  exposure: "comparison" | "monadic",
  cutCount: number,
): StudyDesign {
  return exposure === "monadic" && cutCount >= 2 ? "twin_monadic" : "comparative";
}

/**
 * Respondent ceiling for EVERY design. The raised true-monadic ceiling existed
 * for the split-sample design and retired with it: twin monadic inherits the
 * comparative cap and no design carries a ceiling of its own.
 */
export const DEFAULT_MAX_RESPONDENTS = 1000;

/**
 * How many respondents a study runs when nobody has chosen a sample size.
 *
 * This is not an audience pool. `estimatedRespondentCount` is how many twins
 * exist in the selected audience; launch and draft resume must not copy it.
 */
export const DEFAULT_PLANNED_SAMPLE_SIZE = 100;

/** Reads per cut a monadic design must deliver before it is worth running. */
export const DEFAULT_TARGET_BASE_PER_CUT = 100;

/**
 * Sample size to run.
 *
 * Pass a stored `plannedSampleSize` or an explicit launch `numRespondents`.
 * Do not pass an audience pool (`estimatedRespondentCount`, persona count).
 * A missing, non-integer, or out-of-range value is the product default.
 */
export function resolvePlannedSampleSize(value: unknown): number {
  if (
    typeof value !== "number" ||
    !Number.isInteger(value) ||
    value < 1 ||
    value > DEFAULT_MAX_RESPONDENTS
  ) {
    return DEFAULT_PLANNED_SAMPLE_SIZE;
  }
  return value;
}

export function isStudyDesign(value: unknown): value is StudyDesign {
  return (
    typeof value === "string" &&
    (STUDY_DESIGNS as ReadonlyArray<string>).includes(value)
  );
}

// TRUE_MONADIC_RETIRED: a persisted true-monadic study IS monadic, and every
// road into a NEW run resolves the value through `resolveRetiredStudyDesign`
// before any monadic branch runs, so this arm is a reader, not a behaviour.
export function isMonadicDesign(design: StudyDesign): boolean {
  return design === "true_monadic" || design === "twin_monadic";
}

/**
 * TRUE_MONADIC_RETIRED. The one road out of the retired design: `true_monadic`
 * resolves to `twin_monadic`, every other design passes through untouched.
 *
 * Every producer of a design for a NEW run goes through this helper
 * (`recommendMonadicDesign`, `evaluateStudyDesign`, the `readStampedStudyDesign`
 * consumers, the frontend's `withResolvedDesign`), so nothing downstream ever
 * sees the retired value on a run that has not happened yet. A value read back
 * off a study that already ran is NOT resolved here: analysis and the deck read
 * what that run actually did.
 */
export function resolveRetiredStudyDesign(design: StudyDesign): StudyDesign {
  return design === "true_monadic" ? "twin_monadic" : design;
}

export function maxRespondentsForDesign(design: StudyDesign): number {
  void design;
  return DEFAULT_MAX_RESPONDENTS;
}

/**
 * Respondent count that hits the per-cut target for a design.
 *
 * Twin monadic needs `targetBase` twins because every twin reads every cut in
 * isolation. Comparative and single-cut studies return 0 so callers keep the
 * product default (`DEFAULT_PLANNED_SAMPLE_SIZE`).
 */
export function targetRespondentsForDesign(
  design: StudyDesign,
  cutCount: number,
  targetBasePerCut: number = DEFAULT_TARGET_BASE_PER_CUT,
): number {
  const cuts = positiveInt(cutCount);
  const targetBase = positiveInt(targetBasePerCut) || DEFAULT_TARGET_BASE_PER_CUT;
  if (cuts < 2) {
    return 0;
  }
  if (design === "twin_monadic") {
    return targetBase;
  }
  return 0;
}

/**
 * Every study design is purchasable. Twin monadic bills `numRespondents × cuts`
 * (see `billingCellsForDesign` / `billableUnitsForDesign`); C4 closed the
 * margin hole that previously kept it internal-only.
 */
export function isDesignPurchasable(design: StudyDesign): boolean {
  void design;
  return true;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): Array<unknown> {
  return Array.isArray(value) ? value : [];
}

function collectAssetIds(items: Array<unknown>): Array<string> {
  const seen = new Set<string>();
  const out: Array<string> = [];
  for (const item of items) {
    const assetId = String(asRecord(item).assetId ?? "").trim();
    if (!assetId || seen.has(assetId)) {
      continue;
    }
    seen.add(assetId);
    out.push(assetId);
  }
  return out;
}

function videoAssetIds(testedAssets: unknown): Array<string> {
  const seen = new Set<string>();
  const out: Array<string> = [];
  for (const raw of asArray(asRecord(testedAssets).assets)) {
    const asset = asRecord(raw);
    const hasVideo = asArray(asset.media).some(
      (medium) => asRecord(medium).kind === "video",
    );
    const assetId = String(asset.id ?? "").trim();
    if (!hasVideo || !assetId || seen.has(assetId)) {
      continue;
    }
    seen.add(assetId);
    out.push(assetId);
  }
  return out;
}

/**
 * Ordered stimulus cut ids, mirroring the engine's `stimulus_cut_ids`.
 *
 * New compiled surveys use the ordered canonical catalog, independent of media
 * enrichment. The unmarked branch is exclusively the frozen legacy policy;
 * do not reinterpret an old launch's cut count or charge on read/retry.
 */
export function stimulusCutIds(surveyJson: unknown): Array<string> {
  const survey = asRecord(surveyJson);
  const config = asRecord(survey._canonicalConfig);

  if (readStimulusIdentityPolicy(surveyJson) === STIMULUS_IDENTITY_POLICY) {
    return collectAssetIds(asArray(asRecord(config.assetContext).items));
  }

  const fromVideoContext = collectAssetIds(
    asArray(asRecord(config.assetVideoContext).items),
  );
  if (fromVideoContext.length > 0) {
    return fromVideoContext;
  }

  const fromTestedAssets = videoAssetIds(survey.testedAssets);
  if (fromTestedAssets.length > 0) {
    return fromTestedAssets;
  }

  return collectAssetIds(asArray(asRecord(config.assetContext).items));
}

export function countStimulusCuts(surveyJson: unknown): number {
  return stimulusCutIds(surveyJson).length;
}

/**
 * Whether a lane-zone question opens an independent lane slot, the one fact
 * that makes the lane phase repeat per asset: a `per_asset` binding, or an
 * `all_assets` one under a monadic design. A lane phase with no slot runs once,
 * as shared context. The engine's partition (below), the compiler's exposure
 * scopes and the lane rule of `exposureCoherenceIssues` read this one rule.
 */
export function opensLaneSlot(assetBinding: unknown, monadic: boolean): boolean {
  const mode = asRecord(assetBinding).mode;
  return mode === "per_asset" || (monadic && mode === "all_assets");
}

/**
 * The assets a repeating lane phase runs one lane for (survey-engine
 * `monadic.monadic_lane_asset_ids`): each stimulus asset of the types the lane
 * questions bind, or every stimulus asset when they bind no stimulus type. A
 * catalog asset (a pain, a brand) is never a lane and its type is never a lane
 * type: a pain grid binds assets that are named and never shown, and every
 * lane asks it whole.
 */
export function repeatingLaneAssetIds(
  assets: ReadonlyArray<{ id: string; type: string; stimulus: boolean }>,
  boundTypes: ReadonlyArray<unknown>,
): Array<string> {
  const catalogTypes = new Set(assets.filter(asset => !asset.stimulus).map(asset => asset.type));
  const types = new Set(boundTypes.filter((type): type is string =>
    typeof type === "string" && !catalogTypes.has(type)));
  const out: Array<string> = [];
  for (const asset of assets) {
    if (!asset.stimulus || !asset.id || out.includes(asset.id)) continue;
    if (!types.size || types.has(asset.type)) out.push(asset.id);
  }
  return out;
}

/** Isolated lane identities for a compiled questionnaire with declared zones.
 * Mirrors the engine's typed partition: an independent slot activates the
 * whole lane phase, including its unbound context, and the lanes are
 * `repeatingLaneAssetIds` over the catalog's stamped roles (an item with no
 * role, compiled before roles were stamped, is stimulus). This is execution
 * telemetry, not billing, and deliberately does not reinterpret legacy
 * undeclared phases. */
export function questionnaireLaneAssetIds(surveyJson: unknown): Array<string> {
  const config = asRecord(asRecord(surveyJson)._canonicalConfig);
  const laneQuestions = Object.values(asRecord(config.questionMap))
    .map(asRecord).filter(question => question.exposureZone === "lane");
  const monadic = isMonadicDesign(readStampedStudyDesign(surveyJson) ?? "comparative");
  if (!laneQuestions.some(question => opensLaneSlot(question.assetBinding, monadic))) return [];
  return repeatingLaneAssetIds(
    asArray(asRecord(config.assetContext).items).map(asRecord).map(item => ({
      id: String(item.assetId ?? "").trim(),
      type: String(item.assetType ?? ""),
      stimulus: item.role !== "catalog",
    })),
    laneQuestions.flatMap(question => asArray(asRecord(question.assetBinding).assetTypes)),
  );
}

/** Every tested asset's id, in block order, deduped. */
function testedAssetIds(testedAssets: unknown): Array<string> {
  const seen = new Set<string>();
  const out: Array<string> = [];
  for (const raw of asArray(asRecord(testedAssets).assets)) {
    const assetId = String(asRecord(raw).id ?? "").trim();
    if (!assetId || seen.has(assetId)) {
      continue;
    }
    seen.add(assetId);
    out.push(assetId);
  }
  return out;
}

/**
 * A current editable draft's study units, before compilation. Media/keyframes
 * describe an asset; they never add, remove or reorder an asset identity.
 */
export function stimulusCutIdsForTestedAssets(
  testedAssets: unknown,
): Array<string> {
  return testedAssetIds(testedAssets);
}

export function countStimulusCutsForTestedAssets(
  testedAssets: unknown,
): number {
  return stimulusCutIdsForTestedAssets(testedAssets).length;
}

/**
 * Reads the design already stamped on a survey, using the same three lookup
 * sites the engine reads. Returns null when unset or unrecognised.
 */
export function readStampedStudyDesign(surveyJson: unknown): StudyDesign | null {
  readStimulusIdentityPolicy(surveyJson);
  const survey = asRecord(surveyJson);
  const containers = [
    survey,
    asRecord(survey._templateConfig),
    asRecord(survey._canonicalConfig),
  ];
  for (const container of containers) {
    const raw = container.design;
    if (typeof raw !== "string") {
      continue;
    }
    const normalized = raw.trim().toLowerCase();
    if (isStudyDesign(normalized)) {
      return normalized;
    }
  }
  return null;
}

export interface StudyDesignInput {
  /**
   * The researcher's explicit choice. Blank or omitted resolves to
   * `comparative`: cut count alone must never flip a study to monadic, which
   * would silently monadic-ize existing multi-video comparative studies.
   */
  requestedDesign?: string | null;
  /** Distinct stimulus cuts the engine will see at run time. */
  cutCount: number;
  /** Usable audience pool (materialized twins). null means unknown. */
  personaCount: number | null;
  numRespondents: number;
  /** Reads per cut a monadic design must deliver. Defaults to 100. */
  targetBasePerCut?: number;
  /**
   * @deprecated C4 made twin_monadic purchasable for all accounts. Kept so
   * callers that still pass it keep compiling; ignored by the evaluator.
   */
  internalAccount?: boolean;
}

export type StudyDesignRefusalCode =
  | "UNKNOWN_DESIGN"
  | "NOT_PURCHASABLE"
  | "TOO_FEW_CUTS"
  | "RESPONDENT_CAP"
  | "POOL_UNKNOWN"
  | "POOL_TOO_THIN"
  | "BASE_TOO_SMALL";

export interface StudyDesignRefusal {
  code: StudyDesignRefusalCode;
  /** Whether the refusal is an entitlement problem rather than a shape problem. */
  entitlement: boolean;
  message: string;
}

export interface StudyDesignDecision {
  design: StudyDesign;
  /** True when the caller named the design rather than falling back. */
  explicit: boolean;
  cutCount: number;
  /** Reads each cut receives at this respondent count. */
  basePerCut: number;
  /**
   * Billable read units, retained under the historical field name. These are
   * not the question-level execution reads: comparative per-asset questions
   * can repeat while billing remains one cell per twin.
   */
  totalReads: number;
  maxRespondents: number;
  /** Plain-language base size. No MDE, no power vocabulary — ever. */
  baseSizeLabel: string;
  warnings: Array<string>;
  /** Non-null means the study must be refused, not degraded. */
  refusal: StudyDesignRefusal | null;
}

function positiveInt(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * Billing cells for a design: 1 for comparative / true monadic (one billed
 * read path per twin), N stimulus cuts for twin monadic (each twin rates every
 * cut in an isolated lane).
 */
export function billingCellsForDesign(
  design: StudyDesign,
  cutCount: number,
): number {
  if (design === "twin_monadic") {
    const cuts = positiveInt(cutCount);
    return cuts > 0 ? cuts : 1;
  }
  return 1;
}

/**
 * Billable units charged for a run: `numRespondents × billingCells`.
 * Same billing quantity as `StudyDesignDecision.totalReads` — preview and charge both
 * call this so they cannot drift (QS parity: shared pure math).
 */
export function billableUnitsForDesign(
  design: StudyDesign,
  numRespondents: number,
  cutCount: number,
): number {
  return (
    positiveInt(numRespondents) * billingCellsForDesign(design, cutCount)
  );
}

function basePerCutFor(
  design: StudyDesign,
  numRespondents: number,
  cutCount: number,
): number {
  void design;
  void cutCount;
  // Every design puts every twin in front of every cut: comparative in one
  // session, twin monadic in isolated lanes.
  return numRespondents;
}

/**
 * Plain-language base size. The whole customer-facing surface for design is
 * this one string: "100 twins x 3 cuts (300 isolated reads)" describes what
 * happens without asking a marketer to reason about power.
 */
export function formatBaseSizeLabel(params: {
  design: StudyDesign;
  numRespondents: number;
  cutCount: number;
}): string {
  const twins = positiveInt(params.numRespondents);
  const cuts = positiveInt(params.cutCount);
  const twinsLabel = `${twins} twin${twins === 1 ? "" : "s"}`;

  if (params.design === "twin_monadic" && cuts >= 2) {
    return `${twinsLabel} × ${cuts} cuts (${twins * cuts} isolated reads)`;
  }
  if (cuts >= 2) {
    return `${twinsLabel}, each seeing all ${cuts} cuts`;
  }
  return twinsLabel;
}

/**
 * The default policy for the monadic family: twin monadic at two or more cuts,
 * comparative below that. Pool-size no longer enters the decision — the split
 * sample it used to buy is retired (TRUE_MONADIC_RETIRED), so the only monadic
 * design left is the one where every twin reads every cut in an isolated lane.
 * Twin monadic is purchasable (billed per isolated read).
 *
 * `personaCount` and `targetBasePerCut` are kept in the signature: callers pass
 * them today and `evaluateStudyDesign` still checks the pool against the
 * respondent count.
 */
export function recommendMonadicDesign(params: {
  cutCount: number;
  personaCount: number | null;
  targetBasePerCut?: number;
}): StudyDesign {
  const cutCount = positiveInt(params.cutCount);
  return studyDesignForExposure("monadic", cutCount);
}

/**
 * Resolves the design and decides whether the study may be created.
 *
 * Refuse rather than degrade: a thin pool, a cut count below two, or a per-cut
 * base under target produces a refusal with the number the caller must change.
 * Quick Study silently wraps its partition windows and discloses post-hoc in a
 * footer; that is not acceptable on the surface that produces a report a
 * customer forwards to their CMO.
 */
export function evaluateStudyDesign(
  input: StudyDesignInput,
): StudyDesignDecision {
  const cutCount = positiveInt(input.cutCount);
  const numRespondents = positiveInt(input.numRespondents);
  const targetBase =
    positiveInt(input.targetBasePerCut) || DEFAULT_TARGET_BASE_PER_CUT;
  const pool = input.personaCount == null ? null : positiveInt(input.personaCount);

  const requested =
    typeof input.requestedDesign === "string"
      ? input.requestedDesign.trim().toLowerCase()
      : "";
  const explicit = requested.length > 0;

  if (explicit && !isStudyDesign(requested)) {
    return {
      design: "comparative",
      explicit,
      cutCount,
      basePerCut: numRespondents,
      totalReads: numRespondents,
      maxRespondents: DEFAULT_MAX_RESPONDENTS,
      baseSizeLabel: formatBaseSizeLabel({
        design: "comparative",
        numRespondents,
        cutCount,
      }),
      warnings: [],
      refusal: {
        code: "UNKNOWN_DESIGN",
        entitlement: false,
        message: `Unknown study design "${input.requestedDesign}". Expected one of: ${STUDY_DESIGNS.join(", ")}.`,
      },
    };
  }

  // TRUE_MONADIC_RETIRED: an explicit `true_monadic` is DOWNGRADED, never
  // refused. A legacy re-run names a design we can still run under its retained
  // name, so it launches as twin monadic and the caller is told in a warning.
  const requestedDesign: StudyDesign | null = explicit
    ? (requested as StudyDesign)
    : null;
  const design: StudyDesign =
    requestedDesign === null
      ? "comparative"
      : resolveRetiredStudyDesign(requestedDesign);
  const basePerCut = basePerCutFor(design, numRespondents, cutCount);
  const totalReads = billableUnitsForDesign(design, numRespondents, cutCount);
  const maxRespondents = maxRespondentsForDesign(design);
  const baseSizeLabel = formatBaseSizeLabel({
    design,
    numRespondents,
    cutCount,
  });

  const warnings: Array<string> = [];
  const refusals: Array<StudyDesignRefusal> = [];

  // TRUE_MONADIC_RETIRED. Not a behavioural branch: the design was already
  // resolved through `resolveRetiredStudyDesign` above, so every number below
  // is a twin-monadic number whichever way this goes. All this arm adds is the
  // warning, which is the ONLY signal a caller naming the retired design gets,
  // so it states the price change too: the design moves from one read per twin
  // to one per cut, which is what the run bills (review F1).
  if (requestedDesign === "true_monadic") {
    warnings.push(
      `True monadic is retired; resolved to twin monadic. Every twin now reads every cut in an isolated lane, so this study bills ${totalReads} reads rather than ${numRespondents}.`,
    );
  }

  if (isMonadicDesign(design) && cutCount < 2) {
    refusals.push({
      code: "TOO_FEW_CUTS",
      entitlement: false,
      message: `A monadic design needs at least 2 stimulus cuts; this study has ${cutCount}. Add another cut or run it comparative.`,
    });
  }

  if (numRespondents > maxRespondents) {
    refusals.push({
      code: "RESPONDENT_CAP",
      entitlement: false,
      message: `${numRespondents} respondents exceeds the ${maxRespondents} cap for a ${design} study.`,
    });
  }

  if (isMonadicDesign(design) && pool === null) {
    refusals.push({
      code: "POOL_UNKNOWN",
      entitlement: false,
      message:
        "A monadic design needs a known audience size to size its cells. Select an audience before launching.",
    });
  }

  if (isMonadicDesign(design) && pool !== null && pool < numRespondents) {
    refusals.push({
      code: "POOL_TOO_THIN",
      entitlement: false,
      message: `A monadic design needs one distinct twin per read: ${numRespondents} requested, this audience has ${pool}. Lower the respondent count to ${pool} or pick a larger audience.`,
    });
  }

  // Under-target bases WARN, they do not refuse (Fernando, 2026-08-11). The
  // review step sizes a study one group at a time and states the ceiling the
  // audience allows, so a thin base is now an informed choice rather than a
  // silent degrade — which was the only thing the refusal protected against.
  // Refusing here would also make every audience under `targetBase` unable to
  // run a monadic study at all. The other refusal codes are unchanged.
  if (isMonadicDesign(design) && basePerCut < targetBase) {
    warnings.push(
      `Twin monadic gives ${basePerCut} reads per cut, under the ${targetBase}-per-cut target. Reaching it needs ${targetBase} twins.`,
    );
  }

  if (design === "comparative" && cutCount >= 2) {
    warnings.push(
      `Comparative: all ${cutCount} cuts are shown to every twin in one session.`,
    );
  }

  return {
    design,
    explicit,
    cutCount,
    basePerCut,
    totalReads,
    maxRespondents,
    baseSizeLabel,
    warnings,
    refusal: refusals[0] ?? null,
  };
}
